// The DM's hand edits, kept apart from the sheet.
//
// Requested 2026-10-02: "be able to set/edit any character parameter in the
// party overview - it'll let me set overrides on the fly", with "an option to
// reset to default on any overrides individually or carte blanche".
//
// The important half is the word OVERRIDE. mergeSheet writes every sheet field
// onto the character, so a value typed straight into the record is correct until
// the next sync and then silently gone - and the live poll syncs on its own, so
// it would vanish mid-session with nobody touching anything. A DM would set a
// creature's AC, watch it hold, and discover it was not trustworthy only when it
// failed at a table.
//
// WHICH FIELDS, AND WHY NOT THE REST. The sync is three-way, so anything D&D
// Beyond will accept ought to travel rather than be pinned locally. Its writers
// accept health, conditions, exhaustion, death saves, the Max HP Modifier and
// coin balances - all of which sync all three ways already and none of which is
// overridable here. It will not accept armour class, initiative, passive
// perception, proficiency bonus, saving throws or speed, because it computes
// them from the character's build and exposes no field to set. Those are exactly
// the ones below: an override is for the values that cannot round-trip.
import type { Character } from "./types";

/** Fields the DM may override by hand.
 *
 *  maxHp is deliberately absent. It already has its own editor on the card and
 *  its own sync-aware machinery - maxHpBase and the sheet's Max HP Modifier,
 *  which exist so a drain or an Aid moves the maximum by exactly its delta. An
 *  override on top would be a second answer to the same question, and the two
 *  would disagree the first time a modifier changed. */
export const OVERRIDABLE = ["name", "detail", "ac", "initiative", "passivePerception", "proficiencyBonus", "speed", "languages"] as const;

/** A comma-separated list as the DM types it, back to the array the app reads.
 *  Empty entries are dropped rather than stored, so a trailing comma while
 *  typing does not leave a blank language on the card. */
export const parseList = (text: string): string[] => String(text || "").split(",").map((x) => x.trim()).filter(Boolean);
export type OverridableKey = (typeof OVERRIDABLE)[number];

/** Saving throws and ability scores are overridable too, one at a time. */
export const OVERRIDABLE_ABILITIES = ["str", "dex", "con", "int", "wis", "cha"] as const;

export type Group = "fields" | "savingThrows" | "abilities";

export interface Overrides {
  fields?: Partial<Record<OverridableKey, unknown>>;
  savingThrows?: Partial<Record<string, number>>;
  abilities?: Partial<Record<string, number>>;
  /** The sheet's own answer for each overridden key.
   *
   *  Without this, clearing an override could only delete the DM's value - the
   *  overridden one was already baked into the character record and there was
   *  nothing left to put back, so "reset" left the override's value in place and
   *  merely stopped calling it an override. It is refreshed on every merge, so
   *  it is the sheet's CURRENT answer rather than whatever it said when the
   *  override was first set. */
  base?: Record<string, unknown>;
}

const baseKey = (key: string, group: Group) => (group === "fields" ? key : `${group}:${key}`);

export const overridesOf = (c: Character | undefined): Overrides => ((c as { overrides?: Overrides } | undefined)?.overrides) || {};

/** Is this field currently the DM's answer rather than the sheet's? */
export function isOverridden(c: Character | undefined, key: string, group: Group = "fields"): boolean {
  const o = overridesOf(c)[group] as Record<string, unknown> | undefined;
  return Boolean(o && Object.prototype.hasOwnProperty.call(o, key));
}

export const overrideCount = (c: Character | undefined): number => {
  const o = overridesOf(c);
  return Object.keys(o.fields || {}).length + Object.keys(o.savingThrows || {}).length + Object.keys(o.abilities || {}).length;
};

const hasAny = (o: Overrides) => Boolean(Object.keys(o.fields || {}).length || Object.keys(o.savingThrows || {}).length || Object.keys(o.abilities || {}).length);

/** Record an override, or clear one by passing a blank - which is what a DM does
 *  when they want the sheet's value back and the field is already under their
 *  cursor. Clearing restores the sheet's answer rather than leaving the
 *  override's number sitting there unlabelled. */
/** Is this a key the DM is allowed to override?
 *
 *  The lists above were documentation until a mutant showed it: removing
 *  "languages" from OVERRIDABLE changed nothing, because setOverride stored
 *  whatever key it was handed. A typo would have created an override on a field
 *  nothing reads, invisible except as a count on the card that could not be
 *  reset from anywhere. The lists decide now. */
export function canOverride(key: string, group: Group = "fields"): boolean {
  if (group === "fields") return (OVERRIDABLE as readonly string[]).includes(key);
  return (OVERRIDABLE_ABILITIES as readonly string[]).includes(key);
}

export function setOverride(c: Character, key: string, raw: unknown, group: Group = "fields"): Character {
  // An unknown key is a programming mistake, not a DM one: leave the character
  // exactly as it was rather than storing something nothing will ever read.
  if (!canOverride(key, group)) return c;
  const o: Overrides = { ...overridesOf(c) };
  const bucket = { ...((o[group] as Record<string, unknown>) || {}) };
  const base = { ...(o.base || {}) };
  const bk = baseKey(key, group);
  const record = c as unknown as Record<string, unknown>;
  const blank = raw === "" || raw === null || raw === undefined || (group !== "fields" && !Number.isFinite(Number(raw)));

  const next: Record<string, unknown> = { ...record };

  if (blank) {
    delete bucket[key];
    if (Object.prototype.hasOwnProperty.call(base, bk)) {
      // Put the sheet's own value back where the override had been.
      if (group === "fields") next[key] = base[bk];
      else next[group] = { ...((record[group] as Record<string, unknown>) || {}), [key]: base[bk] };
      delete base[bk];
    }
  } else {
    // Remember what we are covering up, once, so reset has something to give back.
    if (!Object.prototype.hasOwnProperty.call(base, bk)) {
      base[bk] = group === "fields" ? record[key] : ((record[group] as Record<string, unknown>) || {})[key];
    }
    bucket[key] = group === "fields" ? raw : Number(raw);
  }

  if (Object.keys(bucket).length) (o as Record<string, unknown>)[group] = bucket;
  else delete (o as Record<string, unknown>)[group];
  if (Object.keys(base).length) o.base = base; else delete o.base;

  if (hasAny(o)) next.overrides = o; else delete next.overrides;
  return applyOverrides(next as Character);
}

/** Drop every override and give the sheet's values back. */
export function clearOverrides(c: Character): Character {
  const o = overridesOf(c);
  const next: Record<string, unknown> = { ...(c as unknown as Record<string, unknown>) };
  for (const [bk, v] of Object.entries(o.base || {})) {
    const [maybeGroup, maybeKey] = bk.split(":");
    if (maybeKey === undefined) next[bk] = v;
    else next[maybeGroup] = { ...((next[maybeGroup] as Record<string, unknown>) || {}), [maybeKey]: v };
  }
  delete next.overrides;
  return next as Character;
}

/** The character as the DM sees it: the sheet's values with their overrides on
 *  top. Called at the end of every merge, so a sync cannot win. */
export function applyOverrides<T extends Character>(c: T): T {
  const o = overridesOf(c);
  if (!hasAny(o)) return c;
  const next: Record<string, unknown> = { ...(c as unknown as Record<string, unknown>) };
  const base: Record<string, unknown> = { ...(o.base || {}) };

  for (const [k, v] of Object.entries(o.fields || {})) {
    // A different incoming value is the sheet's current answer; keep it so a
    // reset later returns what the sheet says now, not what it said in March.
    if (next[k] !== v) base[k] = next[k];
    next[k] = v;
  }
  for (const group of ["savingThrows", "abilities"] as const) {
    const g = o[group] as Record<string, unknown> | undefined;
    if (!g) continue;
    const cur = { ...((next[group] as Record<string, unknown>) || {}) };
    for (const [k, v] of Object.entries(g)) {
      const bk = baseKey(k, group);
      if (cur[k] !== v) base[bk] = cur[k];
      cur[k] = v;
    }
    next[group] = cur;
  }
  next.overrides = { ...o, base };
  return next as T;
}
